import React, { useEffect, useState, useMemo } from 'react'
import Text from '../../atoms/text/Text'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import ResultsTable from './table/ResultsTable'
import { TeamsWithColours } from './RoundResults'
import { percentage, thousandSeparator } from '../model/utils/formatters'

import './RoundResultsTablePage.scss'
import classNames from 'classnames'
import { useResultsContext } from './context/context'

interface Props {
  teams: TeamsWithColours[]
  dealRoom: ModelAPI.Reporting.DealRoom
}

const RoundResultsDealRoomTable: React.FC<Props>  = ({
  teams,
  dealRoom
}) => {
  const context = useResultsContext()
  const [show, setShow] = useState(context.noAnimations ? true : false)

  const { nonBids, sortedBidsFromHighestToLowest } = useMemo(() => {
    const nonBids = dealRoom.bids.filter(b => b.bid === 0)
    const sortedBidsFromHighestToLowest = dealRoom.bids.filter(b => b.bid > 0).sort((a, b) => b.bid - a.bid)
    return { nonBids, sortedBidsFromHighestToLowest }
  }, [dealRoom.bids])

  const hasReturns = useMemo(() => {
    return dealRoom.bids.some(b => b.return != null)
  }, [dealRoom.bids])

  const [showRow, setShowRow] = useState(context.noAnimations ? 0 : sortedBidsFromHighestToLowest.length)

  useEffect(() => {
    let timer = setTimeout(() => setShow(true), 2000)

    return () => clearTimeout(timer)
  }, [show])

  const noBids = sortedBidsFromHighestToLowest.length === 0

  return (
    <VerticalGroup full center wide>
      <Text className="mb-4" size="6xl" bold colour='primaryDark'>Deal Room Results</Text>
      <InlineGroup
        spread
        fullHeight
        block
        className={classNames('executive-decisions-table', { animate: !context.noAnimations })}
      >
        {noBids ? <InlineGroup fullHeight block center verticalCenter>
          <Text size="5xl" bold>NO BIDS!</Text>
        </InlineGroup> : <><VerticalGroup
          style={{ width: '50%' }}
          className={classNames({
            'show-options': !context.noAnimations && show,
            'pr-4': show,
            'no-show-options': !show
          })}
        >
          <Text
            size="4xl"
            bold
            className="executive-decisions-options-title pb-4"
          >
            {dealRoom.name}
          </Text>
          <VerticalGroup className="pb-4" style={{maxWidth: '600px'}}>
            <Text size="xl" isHtml>{dealRoom.outcome}</Text>
          </VerticalGroup>
        </VerticalGroup>
        <VerticalGroup style={{ width: '50%', height: '100%' }}>
          <ResultsTable id={dealRoom.id}>
            <thead>
              <tr>
                <th>BANK</th>
                <th>BID</th>
                {hasReturns && <th>RETURN</th>}
              </tr>
            </thead>
            <tbody onClick={() => setShowRow(showRow - 1)}>
              {sortedBidsFromHighestToLowest.map((bid, i) => {
                const teamForTable = teams.find((t) => t.teamId === bid.teamId)

                return (
                  <tr key={i} className={showRow <= i ? 'show-bids' : 'no-show-options'}>
                    <td style={{ color: `${teamForTable?.colour}` }}>{teamForTable?.teamName}</td>
                    <td>{thousandSeparator(bid.bid)}</td>
                    {hasReturns && <td>{percentage(bid.return ?? 0, 1)}</td>}
                  </tr>
                )

              })}

              {nonBids.map((bid, i) => {
                const teamForTable = teams.find((t) => t.teamId === bid.teamId)

                return (
                  <tr key={i}>
                    <td style={{ color: `${teamForTable?.colour}` }}>{teamForTable?.teamName}</td>
                    <td>No Bid</td>
                    {hasReturns && <td>-</td>}
                  </tr>
                )
              })}
            </tbody>
          </ResultsTable>
          {/* Added so you know to click on table body to display data */}
          <InlineGroup block center className='executive-decisions-card' >
            <Text center colour='primaryDark' className='mt-2'>Click on table row to display data.</Text>
          </InlineGroup>
        </VerticalGroup></>}
      </InlineGroup>
    </VerticalGroup>
  )
}

export default RoundResultsDealRoomTable
